import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import type { EmailQueueListItemDto } from '../../types/admin';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RetryIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmailQueueDetailDialog from './EmailQueueDetailDialog';

/**
 * Email Queue List component
 * Displays paginated list of emails with filtering and actions
 */
export default function EmailQueueList() {
  const { t } = useTranslation('admin');
  const { token } = useAuth();
  const [emails, setEmails] = useState<EmailQueueListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20
  });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('');
  const [recipientFilter, setRecipientFilter] = useState<string>('');
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    loadEmails();
  }, [paginationModel, statusFilter, emailTypeFilter, recipientFilter]);

  const loadEmails = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getEmailQueue(
        {
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          status: statusFilter || undefined,
          emailType: emailTypeFilter || undefined,
          recipientEmail: recipientFilter || undefined,
          sortBy: 'CreatedAt',
          sortDescending: true
        },
        token
      );
      setEmails(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (id: number) => {
    setSelectedEmailId(id);
    setDetailDialogOpen(true);
  };

  const handleRetry = async (id: number) => {
    if (!token) return;

    try {
      await adminService.retryEmail(id, token);
      loadEmails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry email');
    }
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    if (!confirm(t('emailQueue.confirmDelete'))) return;

    try {
      await adminService.deleteEmail(id, token);
      loadEmails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete email');
    }
  };

  const handleTriggerProcessing = async () => {
    if (!token) return;

    try {
      await adminService.triggerProcessing(token);
      setTimeout(loadEmails, 2000); // Refresh after 2 seconds
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger processing');
    }
  };

  const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'default';
      case 'retrying':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: GridColDef<EmailQueueListItemDto>[] = [
    {
      field: 'id',
      headerName: t('emailQueue.columns.id'),
      width: 80
    },
    {
      field: 'recipientEmail',
      headerName: t('emailQueue.columns.recipient'),
      flex: 1,
      minWidth: 200
    },
    {
      field: 'subject',
      headerName: t('emailQueue.columns.subject'),
      flex: 1,
      minWidth: 200
    },
    {
      field: 'emailType',
      headerName: t('emailQueue.columns.type'),
      width: 130,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      )
    },
    {
      field: 'status',
      headerName: t('emailQueue.columns.status'),
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color={getStatusColor(params.value)} />
      )
    },
    {
      field: 'retryCount',
      headerName: t('emailQueue.columns.retries'),
      width: 100,
      renderCell: (params) => `${params.value} / ${params.row.maxRetryAttempts}`
    },
    {
      field: 'createdAt',
      headerName: t('emailQueue.columns.created'),
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'actions',
      headerName: t('emailQueue.columns.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('emailQueue.actions.viewDetails')}>
            <IconButton size="small" onClick={() => handleViewDetails(params.row.id)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('emailQueue.actions.retry')}>
            <IconButton
              size="small"
              onClick={() => handleRetry(params.row.id)}
              disabled={params.row.status.toLowerCase() === 'delivered'}
            >
              <RetryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('emailQueue.actions.delete')}>
            <IconButton size="small" onClick={() => handleDelete(params.row.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          select
          label={t('emailQueue.filters.status')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t('emailQueue.filters.all')}</MenuItem>
          <MenuItem value="Pending">{t('emailQueue.status.pending')}</MenuItem>
          <MenuItem value="Retrying">{t('emailQueue.status.retrying')}</MenuItem>
          <MenuItem value="Delivered">{t('emailQueue.status.delivered')}</MenuItem>
          <MenuItem value="Failed">{t('emailQueue.status.failed')}</MenuItem>
        </TextField>

        <TextField
          select
          label={t('emailQueue.filters.emailType')}
          value={emailTypeFilter}
          onChange={(e) => setEmailTypeFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t('emailQueue.filters.all')}</MenuItem>
          <MenuItem value="Confirmation">Confirmation</MenuItem>
          <MenuItem value="MagicLink">Magic Link</MenuItem>
          <MenuItem value="Mention">Mention</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>

        <TextField
          label={t('emailQueue.filters.recipient')}
          value={recipientFilter}
          onChange={(e) => setRecipientFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          placeholder="email@example.com"
        />

        <Box flexGrow={1} />

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadEmails}
          disabled={loading}
        >
          {t('emailQueue.actions.refresh')}
        </Button>

        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handleTriggerProcessing}
          disabled={loading}
        >
          {t('emailQueue.actions.processNow')}
        </Button>
      </Box>

      <DataGrid
        rows={emails}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50, 100]}
        rowCount={totalCount}
        paginationMode="server"
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer'
          }
        }}
      />

      {selectedEmailId && (
        <EmailQueueDetailDialog
          emailId={selectedEmailId}
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedEmailId(null);
          }}
          onRetry={() => {
            handleRetry(selectedEmailId);
            setDetailDialogOpen(false);
            setSelectedEmailId(null);
          }}
        />
      )}
    </Box>
  );
}
